// @ts-check
import React, { useState } from 'react';

import Button from '@liquidweb-dev/undercurrent/Button';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import Drawer from '@liquidweb-dev/undercurrent/Drawer';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import LWForm from 'components/LWForm';
import { HookedServerSpecsCard } from 'components/SeverSpecsCard';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';
import { actions as cloneActions } from 'modules/basket/addItemForClone';
import cartSelectors from 'modules/cart/selectors';
import useAssetDetails from 'modules/queries/asset/useDetails';
import { useDispatch, useSelector } from 'react-redux';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import { DrawerAbandonCartContents } from '../DrawerAbandonCart/DrawerAbandonCart';
import { HookedCheckbox, HookedSubmit } from '../Form';

/** @param {Omit<import('@liquidweb-dev/undercurrent/Drawer').DrawerProps, 'onClose'> & { onClose: () => void, uniqId: string }} drawerProps */
const DrawerCloneServer = ({ onClose, uniqId, open, ...drawerProps }) => {
	const dispatch = useDispatch();

	const initialValues = { confirm: false };
	const [formValues, setFormValues] = useState(initialValues);
	const [formErrors, setFormErrors] = useState({});
	const [showAbandonCartContents, setShowAbandonCartContents] = useState(false);

	const { data: assetDetails } = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: hostingDetailsAlsowith,
		},
		{ enabled: Boolean(open) },
	);

	const { displayType, featureDetails, type = '' } = assetDetails || {};

	const isCartLocked = useSelector(cartSelectors.getIsCartLocked);

	const handleClose = () => {
		onClose();
		setShowAbandonCartContents(false);
		setFormValues({ confirm: false });
		setFormErrors({});
	};

	const addItemForClone = () => {
		dispatch(
			cloneActions.addItemForClone({
				uniqId,
				force: !featureDetails,
				productCode: type,
			}),
		);
	};

	const handleSubmit = () => {
		if (isCartLocked) {
			setShowAbandonCartContents(true);
		} else {
			handleClose();
			addItemForClone();
		}
	};

	if (showAbandonCartContents) {
		return (
			<Drawer anchor="right" onClose={handleClose} open={open} {...drawerProps}>
				<DrawerAbandonCartContents
					onAbandoned={addItemForClone}
					onClose={handleClose}
				/>
			</Drawer>
		);
	}

	return (
		<Drawer anchor="right" onClose={handleClose} open={open} {...drawerProps}>
			<DrawerHeader onClose={handleClose}>Clone Server</DrawerHeader>
			<LWForm
				values={formValues}
				onValuesChange={setFormValues}
				errors={formErrors}
				onErrorsChange={setFormErrors}
				onSubmit={handleSubmit}
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					height: '100%',
				}}
			>
				<DrawerContent>
					<Typography variant="body-md">
						A new {displayType} will be added to your cart with an identical
						configuration to this one. You will be able to change it before
						checkout.
					</Typography>

					<HookedServerSpecsCard
						uniqId={uniqId}
						cardHeaderTitle="Selected server to clone"
					/>

					<Card>
						<CardContent
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 200,
								padding: 200,
							}}
						>
							<Stack
								sx={{
									gap: 50,
								}}
							>
								<Typography
									variant="body-md"
									sx={{
										fontWeight: 500,
									}}
								>
									Potential for lost data
								</Typography>
								<Typography variant="body-sm">
									After checkout, all data will be copied from the original
									server to the clone server.{' '}
									<b>
										If the current server is busy, this could lead to cloning
										incomplete files and corrupted data.
									</b>
								</Typography>
							</Stack>
							<HookedCheckbox
								name="confirm"
								label="Confirm and acknowledge"
								required
							/>
						</CardContent>
					</Card>
				</DrawerContent>
				<DrawerFooter sx={{ gap: 100 }}>
					<HookedSubmit variant="primary" size="large">
						Add to cart
					</HookedSubmit>
					<Button variant="ghost" onClick={handleClose} size="large">
						Cancel
					</Button>
				</DrawerFooter>
			</LWForm>
		</Drawer>
	);
};

export default DrawerCloneServer;
